import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import Modal from "./Modal";

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isGetStartedOpen, setIsGetStartedOpen] = useState(false);
  const [isScrollingDown, setIsScrollingDown] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // Hide header when scrolling down, show when scrolling up
      if (currentScrollY > lastScrollY && currentScrollY > 50) {
        setIsScrollingDown(true);
      } else {
        setIsScrollingDown(false);
      }
      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY]);

  // Close menu when clicking a link
  const handleLinkClick = () => {
    setIsMenuOpen(false);
  };

  const navLinks = [
    { label: "Home", path: "/" },
    { label: "Services", path: "/services" },
    { label: "About", path: "/about" },
    { label: "Contact", path: "/contact" },
  ];

  return (
    <>
      <header
        className={`sticky top-0 z-40 transition-all duration-300 bg-transparent ${
          isScrollingDown ? "-translate-y-full" : "translate-y-0"
        }`}
      >
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <Link
              to="/"
              className="flex items-center gap-2 group flex-shrink-0 z-50"
            >
              <img
                src="https://cdn.builder.io/api/v1/image/assets%2F8d83d7eadecb4f44a79c56aae0b6143a%2Fedf7a02b36b94a74aab77977a7342137?format=webp&width=200&height=300"
                alt="Alkane Engineering Limited"
                className="h-20 w-auto transition-transform group-hover:scale-105 drop-shadow-lg"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="text-white font-medium text-sm transition-colors hover:text-primary drop-shadow-md whitespace-nowrap"
                >
                  {link.label}
                </Link>
              ))}
              <button
                onClick={() => setIsGetStartedOpen(true)}
                className="bg-primary text-white px-6 py-2.5 rounded-lg font-semibold text-sm transition-all hover:shadow-lg hover:shadow-primary/40 hover:-translate-y-0.5 active:scale-95 whitespace-nowrap"
              >
                Get Started
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden text-white p-2 transition-colors hover:bg-white/10 rounded-lg drop-shadow-lg z-50"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </nav>

        {/* Mobile Navigation - Slide in from right */}
        {isMenuOpen && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/50 backdrop-blur-sm md:hidden z-40 animate-fade-in"
              onClick={() => setIsMenuOpen(false)}
            />

            {/* Slide-in Menu */}
            <div className="fixed top-0 right-0 h-screen w-full max-w-sm bg-gray-900 shadow-2xl md:hidden z-40 animate-slide-in-right overflow-y-auto">
              {/* Menu Header */}
              <div className="flex justify-between items-center p-6 border-b border-gray-800">
                <h2 className="text-white font-semibold text-lg">Menu</h2>
                <button
                  onClick={() => setIsMenuOpen(false)}
                  className="text-white p-2 hover:bg-white/10 rounded-lg transition-colors"
                  aria-label="Close menu"
                >
                  <X size={24} />
                </button>
              </div>

              {/* Navigation Links */}
              <div className="p-6 space-y-2">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={handleLinkClick}
                    className="block w-full text-white font-medium py-3 px-4 rounded-lg transition-all hover:bg-primary/20 hover:text-primary hover:translate-x-2 active:bg-primary/30"
                  >
                    {link.label}
                  </Link>
                ))}

                {/* Divider */}
                <div className="my-4 h-px bg-gray-700" />

                {/* Get Started Button in Mobile Menu */}
                <button
                  onClick={() => {
                    setIsGetStartedOpen(true);
                    setIsMenuOpen(false);
                  }}
                  className="w-full bg-primary text-white py-3 px-4 rounded-lg font-semibold transition-all hover:shadow-lg hover:shadow-primary/40 active:scale-95 mt-4"
                >
                  Get Started
                </button>

                {/* Contact Info in Mobile Menu */}
                <div className="mt-8 p-4 bg-gray-800 rounded-lg border border-gray-700">
                  <p className="text-gray-400 text-xs font-semibold uppercase mb-3">
                    Quick Contact
                  </p>
                  <a
                    href="tel:+234802556883"
                    className="text-white font-semibold text-sm hover:text-primary transition-colors block mb-2"
                  >
                    +234 (0) 802 556 8836
                  </a>
                  <a
                    href="mailto:info@alkaneseengineering.com"
                    className="text-gray-400 text-sm hover:text-primary transition-colors break-all"
                  >
                    info@alkaneseengineering.com
                  </a>
                </div>
              </div>
            </div>
          </>
        )}
      </header>

      {/* Get Started Modal */}
      <Modal
        isOpen={isGetStartedOpen}
        onClose={() => setIsGetStartedOpen(false)}
        title="Get Started Today"
        size="md"
      >
        <div className="space-y-6">
          <p className="text-gray-600 text-lg">
            Ready to transform your project with Alkane Engineering? Let's get
            started on your journey to success.
          </p>
          <form className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Full Name
              </label>
              <input
                type="text"
                placeholder="Your name"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Email Address
              </label>
              <input
                type="email"
                placeholder="your@email.com"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Project Type
              </label>
              <select className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary">
                <option>Select a service</option>
                <option>Engineering</option>
                <option>Procurement</option>
                <option>Construction</option>
                <option>Installation</option>
                <option>Equipment Leasing</option>
                <option>Safety Equipment</option>
              </select>
            </div>
            <button
              type="submit"
              className="w-full bg-primary text-white py-3 rounded-lg font-semibold transition-all hover:shadow-lg hover:shadow-primary/40 active:scale-95"
            >
              Schedule Consultation
            </button>
          </form>
        </div>
      </Modal>
    </>
  );
}
